import { useState } from "react";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/Button";
import { useT } from "../i18n";
import { authStore } from "./authStore";

type Mode = "login" | "register";

export function AuthScreen({
  initialMode = "login",
  onSuccess,
}: {
  initialMode?: Mode;
  onSuccess?: () => void;
}) {
  const t = useT();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);

  const finish = () => {
    if (onSuccess) onSuccess();
    else window.location.hash = "#/";
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result =
        mode === "login"
          ? await authStore.login(email, password)
          : await authStore.register(email, password);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      if ("recoveryCode" in result && result.recoveryCode) {
        setRecoveryCode(String(result.recoveryCode));
        return;
      }
      finish();
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCode) {
    return (
      <div className="auth-shell">
        <div className="auth-card screen-enter">
          <BrandMark />
          <header className="auth-card__head">
            <h1>{t("auth.recoveryTitle")}</h1>
            <p>{t("auth.recoveryText")}</p>
          </header>
          <p
            className="field__input"
            style={{ fontFamily: "ui-monospace, monospace", letterSpacing: "0.06em", textAlign: "center" }}
          >
            {recoveryCode}
          </p>
          <Button
            type="button"
            onClick={() => {
              setRecoveryCode(null);
              finish();
            }}
          >
            {t("auth.savedContinue")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card screen-enter">
        <BrandMark />
        <header className="auth-card__head">
          <h1>{mode === "login" ? t("auth.welcomeBack") : t("auth.createAccount")}</h1>
          <p>{t("auth.intro")}</p>
        </header>

        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={`auth-tab${mode === "login" ? " is-active" : ""}`}
            onClick={() => {
              setMode("login");
              setError(null);
            }}
          >
            {t("auth.tabLogin")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={`auth-tab${mode === "register" ? " is-active" : ""}`}
            onClick={() => {
              setMode("register");
              setError(null);
            }}
          >
            {t("auth.tabRegister")}
          </button>
        </div>

        <form className="auth-form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">{t("auth.handle")}</span>
            <input
              className="field__input"
              type="text"
              autoComplete="username"
              inputMode="email"
              placeholder={t("auth.handlePlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">{t("auth.passphrase")}</span>
            <input
              className="field__input"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder={t("auth.passphrasePlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>

          {error ? <p className="auth-error">{error}</p> : null}

          <Button type="submit" disabled={busy}>
            {busy
              ? t("common.pleaseWait")
              : mode === "login"
                ? t("auth.submitLogin")
                : t("auth.submitRegister")}
          </Button>
        </form>

        <p className="auth-footnote">
          <a href="#/">{t("auth.backWelcome")}</a>
        </p>
      </div>
    </div>
  );
}
